import { useEffect, useState } from "react";
import api, { fmtDate } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { Database, Zap, Mail, Send, CheckCircle2, XCircle, MinusCircle } from "lucide-react";

const fmtSize = (b) => {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
};

const Card = ({ icon: Icon, title, children, testid, tone }) => (
  <div
    className={`border bg-card p-4 ${tone === "warn" ? "border-primary" : ""}`}
    data-testid={testid}
  >
    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-2">
      <Icon size={12} className={tone === "warn" ? "text-primary" : ""} />
      {title}
    </div>
    {children}
  </div>
);

const StatusPill = ({ ok, labelOk, labelKo }) => (
  <span
    className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${
      ok ? "border-emerald-500 text-emerald-600" : "border-muted-foreground text-muted-foreground"
    }`}
  >
    {ok ? <CheckCircle2 size={10} /> : <MinusCircle size={10} />}
    {ok ? labelOk : labelKo}
  </span>
);

export default function SystemHealth() {
  const { t } = useI18n();
  const [h, setH] = useState(null);

  useEffect(() => {
    api.get("/system/health").then((r) => setH(r.data)).catch(() => {});
  }, []);

  if (!h) return null;

  const backupFile = h.backup.last_file;
  const lastMrp = h.mrp.last_run;
  const em = h.emails;
  const totals = h.mrp.totals;

  return (
    <div className="mb-6" data-testid="system-health-panel">
      <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-2">
        {t("sh.title")}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {/* Last backup */}
        <Card icon={Database} title={t("sh.lastBackup")} testid="sh-backup-card"
              tone={!backupFile ? "warn" : undefined}>
          {backupFile ? (
            <>
              <div className="text-sm font-black tabular-nums">{fmtDate(backupFile.created_at)}</div>
              <div className="text-[11px] text-muted-foreground truncate" title={backupFile.name}>{backupFile.name}</div>
              <div className="text-[11px] text-muted-foreground tabular-nums">{fmtSize(backupFile.size)}</div>
              <div className="mt-2">
                <StatusPill ok={h.backup.schedule.enabled}
                            labelOk={`${t("sh.autoBackup")} · ${t("sh.enabled")} (${t("sh.every")} ${h.backup.schedule.interval_hours}${t("sh.hours")})`}
                            labelKo={`${t("sh.autoBackup")} · ${t("sh.disabled")}`} />
              </div>
            </>
          ) : (
            <div className="text-xs text-muted-foreground">{t("sh.none")}</div>
          )}
        </Card>

        {/* Last MRP run */}
        <Card icon={Zap} title={t("sh.lastMrp")} testid="sh-mrp-card"
              tone={!lastMrp ? "warn" : undefined}>
          {lastMrp ? (
            <>
              <div className="text-sm font-black tabular-nums">{fmtDate(lastMrp.run_at)}</div>
              <div className="text-[11px] text-muted-foreground">
                {lastMrp.mode === "auto" ? t("sh.modeAuto") : t("sh.modeManual")} · {lastMrp.po_count} {t("sh.pos")}
              </div>
              <div className="text-[11px] text-muted-foreground truncate" title={lastMrp.by}>{t("sh.by")}: {lastMrp.by}</div>
              <div className="mt-2">
                <StatusPill ok={h.mrp.schedule.enabled}
                            labelOk={`${t("sh.autoMrp")} · ${t("sh.enabled")} (${t("sh.every")} ${h.mrp.schedule.interval_hours}${t("sh.hours")})`}
                            labelKo={`${t("sh.autoMrp")} · ${t("sh.disabled")}`} />
              </div>
            </>
          ) : (
            <div className="text-xs text-muted-foreground">{t("sh.none")}</div>
          )}
        </Card>

        {/* Emails */}
        <Card icon={Mail} title={t("sh.emails")} testid="sh-emails-card"
              tone={em.error > 0 ? "warn" : undefined}>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <div className="flex items-center gap-1 text-[10px] font-bold uppercase text-emerald-600">
                <CheckCircle2 size={10} /> {t("sh.emailsSent")}
              </div>
              <div className="text-xl font-black tabular-nums" data-testid="sh-emails-sent">{em.sent}</div>
            </div>
            <div>
              <div className="flex items-center gap-1 text-[10px] font-bold uppercase text-destructive">
                <XCircle size={10} /> {t("sh.emailsFailed")}
              </div>
              <div className="text-xl font-black tabular-nums text-destructive" data-testid="sh-emails-failed">{em.error}</div>
            </div>
            <div>
              <div className="flex items-center gap-1 text-[10px] font-bold uppercase text-muted-foreground">
                <MinusCircle size={10} /> {t("sh.emailsNo")}
              </div>
              <div className="text-xl font-black tabular-nums text-muted-foreground" data-testid="sh-emails-noaddr">{em.no_email}</div>
            </div>
          </div>
          <div className="mt-2 text-[11px] text-muted-foreground">
            {t("sh.mrpTotals").replace("{runs}", totals.runs).replace("{pos}", totals.pos_created)}
          </div>
        </Card>

        {/* Resend status */}
        <Card icon={Send} title={t("sh.resend")} testid="sh-resend-card"
              tone={!h.resend.configured ? "warn" : undefined}>
          <div className="mb-2">
            <StatusPill ok={h.resend.configured} labelOk={t("sh.configured")} labelKo={t("sh.notConfigured")} />
          </div>
          <div className="text-[11px] text-muted-foreground truncate" title={h.resend.sender || ""}>
            {h.resend.sender || "—"}
          </div>
        </Card>
      </div>

      {/* Recent MRP runs table */}
      {h.mrp.recent_runs.length > 0 && (
        <div className="border bg-card p-4" data-testid="sh-recent-runs">
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-3">
            {t("sh.recentRuns")}
          </div>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t("common.date")}</th>
                  <th>{t("sh.mode")}</th>
                  <th>{t("sh.by")}</th>
                  <th className="num">{t("sh.pos")}</th>
                  <th className="num">{t("sh.emailsSent")}</th>
                  <th className="num">{t("sh.emailsFailed")}</th>
                  <th className="num">{t("sh.emailsNo")}</th>
                </tr>
              </thead>
              <tbody>
                {h.mrp.recent_runs.map((r, i) => (
                  <tr key={i} data-testid={`sh-run-row-${i}`}>
                    <td className="text-muted-foreground tabular-nums">{fmtDate(r.run_at)}</td>
                    <td className="font-bold">{r.mode === "auto" ? t("sh.modeAuto") : t("sh.modeManual")}</td>
                    <td className="text-muted-foreground">{r.by}</td>
                    <td className="num font-bold tabular-nums">{r.po_count}</td>
                    <td className="num tabular-nums text-emerald-600">{r.emails_sent}</td>
                    <td className={`num tabular-nums ${r.emails_failed > 0 ? "text-destructive font-bold" : ""}`}>{r.emails_failed}</td>
                    <td className="num tabular-nums text-muted-foreground">{r.emails_no_email}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
